import { AxiosError } from 'axios';

export interface FormErrors {
    /** The first message for each field the form shows, by field name. */
    fieldErrors: Record<string, string>;
    /** The message shown under the whole form, or null when every error sits by its field. */
    formError: string | null;
}

/**
 * Turn a failed save into the messages a form shows.
 *
 * A 403 gets the `forbidden` sentence. A validation error goes next to its
 * field. A validation error on a field the form does not show goes under the
 * whole form, because otherwise nobody would see it. Anything else shows the
 * server's message, or `fallback` when there is none.
 */
export function formErrorsFrom(error: unknown, shownFields: readonly string[], messages: { forbidden: string; fallback: string }): FormErrors {
    const data = error instanceof AxiosError ? (error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined) : undefined;
    if (error instanceof AxiosError && error.response?.status === 403) {
        return { fieldErrors: {}, formError: messages.forbidden };
    }
    if (data?.errors) {
        const fieldErrors = Object.fromEntries(Object.entries(data.errors).map(([name, fieldMessages]) => [name, fieldMessages[0] ?? '']));
        const hidden = Object.keys(data.errors).find((name) => !shownFields.includes(name));
        return { fieldErrors, formError: hidden === undefined ? null : (data.errors[hidden][0] ?? data.message ?? null) };
    }
    return { fieldErrors: {}, formError: data?.message ?? messages.fallback };
}
